import { useState } from 'react'

export interface CreateCompanyRequest {
  companyName: string
  description?: string
  website?: string
  logoUrl?: string
  // Business fields from E18-S3
  employeeCount: number
  revenueRange?: string
  businessBranches?: string
  aiPainPoints: string
  aiAdoptionStage?: string
  // Admin user fields
  adminEmail: string
  adminPassword: string
  adminName: string
}

export interface Company {
  id: string
  name: string
  description: string | null
  website: string | null
  logoUrl: string | null
  isOnboarded: boolean
  isDemo: boolean
  // Contact fields
  contactPersonName: string | null
  contactEmail: string | null
  phone: string | null
  // Address fields
  addressStreet: string | null
  addressCity: string | null
  addressPostalCode: string | null
  addressCountry: string | null
  // Business fields from E18-S3
  employeeCount: number | null
  industry: string | null
  revenueRange: string | null
  businessBranches: string | null
  aiPainPoints: string | null
  aiAdoptionStage: string | null
}

export interface AdminUser {
  id: string
  email: string
  name: string
  role: string
  companyId: string
}

export interface CreateCompanyResponse {
  message: string
  company: Company
  adminUser: AdminUser
}

export interface UseCreateCompanyResult {
  createCompany: (request: CreateCompanyRequest) => Promise<CreateCompanyResponse>
  loading: boolean
  error: string | null
}

export function useCreateCompany(): UseCreateCompanyResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createCompany = async (request: CreateCompanyRequest): Promise<CreateCompanyResponse> => {
    try {
      setLoading(true)
      setError(null)

      // Get auth token
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      const response = await fetch('/api/admin/companies', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `Failed to create company (${response.status})`)
      }

      const data: CreateCompanyResponse = await response.json()
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create company'
      setError(errorMessage)
      console.error('Error creating company:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    createCompany,
    loading,
    error
  }
}
